import { useCallback, useEffect, useRef } from 'react';
import isEmpty from 'lodash/isEmpty';
import noop from 'lodash/noop';
import omit from 'lodash/omit';
import { log } from '@atlassian/jira-common-util-logging';
import onCreateClick from '@atlassian/jira-create-issue-legacy/src';
import { ff } from '@atlassian/jira-feature-flagging';
import { useTriggerIssueCreateModal } from '@atlassian/jira-issue-create';
import { useResource } from '@atlassian/jira-router';
import { gicOptInStatusResources } from '@atlassian/jira-router-resources-global-issue-create-switch';
import { transformGICPayload, extractEventAttributes, filterCustomFields } from './utils';

const NewIssueCreateTrigger = (): null => {
	const { data } = useResource(gicOptInStatusResources);
	const subscriptionHandle = useRef<() => void>(noop);
	const [, { openIssueCreateModal }] = useTriggerIssueCreateModal();
	const currentIssueCreateVisibility = data?.gicOptInStatus;

	const openIssueCreateDialogHandler = useCallback(
		// @ts-expect-error - TS7006 - Parameter '_payload' implicitly has an 'any' type.
		(event: unknown, _payload) => {
			let payload;
			if (event instanceof CustomEvent && !_payload) {
				payload = event.detail;
			} else {
				payload = _payload;
			}
			const actualPayload = payload?.payload;
			const triggerPointKey = payload?.triggerPointKey;
			const isEcosystemAPI = !!actualPayload?.isEcosystemAPI;
			// EXAMPLE of payload not to be transformed
			// {
			//     shouldNotTransform: true,
			//     payload: {
			//         customFieldsToPreserveOnFormRecreation: ['issuelinks'],
			//         defaultValues: {
			//             issueLinks: {
			//                 linkedIssueKeys: ['IC-31'],
			//             },
			//         },
			//     },
			// },
			if (currentIssueCreateVisibility !== null) {
				if (currentIssueCreateVisibility?.isEnabled) {
					const extractedPayload = actualPayload?.target
						? extractEventAttributes(actualPayload.target)
						: actualPayload;

					let createModalPayload = null;

					if (!isEmpty(extractedPayload)) {
						if (payload?.shouldNotTransform === true) {
							createModalPayload = extractedPayload;
						} else {
							createModalPayload = transformGICPayload(extractedPayload, isEcosystemAPI);
						}
					}
					// extract params

					if (ff('connect-ecosystem-api-modern-issue-create_ccz1b')) {
						const prefilledCustomFields = filterCustomFields(extractedPayload);
						openIssueCreateModal({
							...omit(payload, ['shouldNotTransform']),
							payload: createModalPayload,
							triggerPointKey,
							prefilledCustomFields,
						});
					} else {
						openIssueCreateModal({
							...omit(payload, ['shouldNotTransform']),
							payload: createModalPayload,
							triggerPointKey,
						});
					}
				} else {
					onCreateClick(omit(payload, ['shouldNotTransform']));
				}
			} else {
				log.safeErrorWithoutCustomerData(
					'new-issue-create-trigger',
					'visibility data is still getting downloaded',
				);
			}
		},
		[currentIssueCreateVisibility, openIssueCreateModal],
	);

	useEffect(() => {
		if (currentIssueCreateVisibility !== null) {
			window.IssueCreate = window.IssueCreate || {};
			window.IssueCreate.Visibility = currentIssueCreateVisibility; // For monolith to consume the same
		}
	}, [currentIssueCreateVisibility]);

	useEffect(() => {
		// @ts-expect-error - TS2769 - No overload matches this call.
		document.addEventListener('openIssueCreateDialog', openIssueCreateDialogHandler);
		subscriptionHandle.current = () =>
			// @ts-expect-error - TS2769 - No overload matches this call.
			document.removeEventListener('openIssueCreateDialog', openIssueCreateDialogHandler);

		return () => {
			subscriptionHandle.current();
			subscriptionHandle.current = noop;
		};
	}, [openIssueCreateDialogHandler, subscriptionHandle]);

	return null;
};

export default NewIssueCreateTrigger;
